import { useEffect, useState } from "react";
import { Row, Button, Col, Form, Alert, Card } from "react-bootstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useCart } from "../../contexts/Cart";
import { cartCalculations } from "../Cart";
import localizedLink from "../../utils/localizedLink";
import productTitle from "../../utils/productTitle";
import withPolicyLinks from "../../utils/withPolicyLinks";
import { useTranslation } from "react-i18next";
import useLanguage from "../../hooks/useLanguage";

export default function Order() {
  const navigate = useNavigate();
  const { cartItems } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get("status");
  const [loading, setLoading] = useState(false);
  const { i18n, t } = useTranslation();
  const { language } = useLanguage();

  useEffect(() => {
    if (!status && !cartItems.length) {
      navigate("/");
    }
  }, [cartItems]);

  const handleSubmit = async (event) => {
    try {
      event.preventDefault();

      const fullName = event.target.fullName.value;
      const idNumber = event.target.idNumber.value;
      const address = event.target.address.value;
      const email = event.target.email.value;
      const phoneNumber = event.target.phoneNumber.value;

      if (!cartItems.length) {
        alert(t("order.cart-empty"));
        return;
      }

      if (
        // fullName?.length < 3 ||
        !idNumber?.length ||
        !address ||
        !phoneNumber?.length
      ) {
        alert(t("order.fill-in-required-fields"));
        return;
      }

      setLoading(true);
      const form = {
        orderId: Date.now(),
        fullName,
        idNumber,
        address,
        email,
        phoneNumber,
        cart: cartItems.map((item) => ({
          quantity: item.quantity,
          id: item.id,
          price: item.price,
        })),
      };

      const response = await (
        await fetch(`https://api.eufy.ge/pay?lang=${language}`, {
          method: "POST",
          body: JSON.stringify(form),
          headers: {
            "Content-Type": "application/json",
          },
        })
      ).json();

      // Redirect to payments or order details page
      if (response?.paymentUrl) {
        return (window.location.href = response.paymentUrl);
      } else {
        alert(
          "დაფიქსირდა ტექნიკური ხარვეზი, გთხოვთ შეავსოთ ყველა ველი და სცადოთ ხელახლა ან დაგვიკავშირდით ნომერზე: 574 180 111"
        );
      }
    } catch (error) {
      console.log(error);
      alert(
        "დაფიქსირდა ტექნიკური ხარვეზი, გთხოვთ შეავსოთ ყველა ველი და სცადოთ ხელახლა ან დაგვიკავშირდით ნომერზე: 574 180 111"
      );
    } finally {
      setLoading(false);
    }
  };

  const { totalQuantity, totalFullPrice, totalPrice, totalDiscount } =
    cartCalculations(cartItems);

  return (
    <div className="mt-5">
      {cartItems.length > 0 && (
        <>
          <Row>
            <Col md={12} lg={6} className="mx-auto">
              <Card className="eufy-card mb-3">
                <Card.Body className="d-flex flex-column">
                  {cartItems.map((product, key) => (
                    <div
                      key={product.id}
                      className="mt-2 mb-2 d-flex justify-content-between align-items-center"
                    >
                      <div>
                        <p className="text-sm">
                          <Link
                            to={localizedLink(`/${product.id}`, i18n.language)}
                          >
                            <span className="rounded bg-white px-1.5 py-0.5">
                              {product.quantity}x
                            </span>{" "}
                            {productTitle(product, language)}
                          </Link>
                        </p>
                      </div>
                      <div>
                        <p className="text-sm d-flex align-items-center">
                          {product.oldPrice !== product.price && (
                            <span className="mr-2">
                              <strike className="decoration-1 opacity-60">
                                {product.oldPrice} ₾
                              </strike>
                            </span>
                          )}
                          <span>{product.price} ₾</span>
                        </p>
                      </div>
                    </div>
                  ))}
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col md={12} lg={6} className="mx-auto">
              <Card className="eufy-card mb-3">
                <Card.Body className="d-flex flex-column text-base">
                  <div className="text-sm d-flex justify-content-between align-items-center w-full">
                    <p>
                      {t("order.products")}{" "}
                      <span className="text-sm">({totalQuantity})</span>
                    </p>
                    <p>{totalFullPrice} ₾</p>
                  </div>
                  <div className="text-sm d-flex justify-content-between align-items-center w-full">
                    <p>{t("order.discount")}</p>
                    <p className="text-green-700">
                      {totalDiscount === 0
                        ? `${totalDiscount}`
                        : `-${totalDiscount}`}{" "}
                      ₾
                    </p>
                  </div>
                  <div className="text-sm d-flex justify-content-between align-items-center w-full">
                    <p>{t("order.delivery")}</p>
                    <p>0 ₾</p>
                  </div>
                  <hr className="mt-3 mb-3" />
                  <div className="font-bold text-lg d-flex justify-content-between align-items-center w-full">
                    <p>{t("order.total")}</p>
                    <p>{totalPrice} ₾</p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
      <Row>
        <Col md={12} lg={6} className="mx-auto">
          {status === "successful" && (
            <div className="alert alert-success text-center">
              <strong>{t("order.payment-success")}</strong>
              <p className="mt-2">{t("order.order-processing-note")}</p>
              <Link
                to={localizedLink("/", language)}
                className="mt-3 btn btn-light"
              >
                {t("order.home")}
              </Link>
            </div>
          )}
          {status === "failed" && (
            <div className="alert alert-danger text-center">
              <strong>{t("order.payment-failed")}</strong>
              <p className="mt-2">{t("order.payment-retry")}</p>
              <Link
                to={localizedLink("/", language)}
                className="mt-3 btn btn-light"
              >
                {t("order.home")}
              </Link>
            </div>
          )}

          {loading && (
            <div className="text-center mt-5">
              <div
                className="spinner-border text-secondary"
                style={{ width: "2.5rem", height: "2.5rem" }}
                role="status"
              >
                <span className="visually-hidden">{t("order.loading")}</span>
              </div>
            </div>
          )}

          {!status && !loading && (
            <Card className="eufy-card mb-3">
              <Card.Body className="d-flex flex-column text-base">
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("order.full-name")}</Form.Label>
                    <Form.Control type="text" name="fullName" />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>{t("order.personal-number")}</Form.Label>
                    <Form.Control type="text" name="idNumber" />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>{t("order.email")}</Form.Label>
                    <Form.Control type="text" name="email" />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>{t("order.phone-number")}</Form.Label>
                    <Form.Control type="text" name="phoneNumber" />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>{t("order.country")}</Form.Label>
                    <Form.Select aria-label={t("order.country")}>
                      <option>{t("order.georgia")}</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>{t("order.full-address")}</Form.Label>
                    <Form.Control type="text" name="address" />
                  </Form.Group>

                  <Alert variant="secondary" className="rounded-xl border-0 bg-white">
                    <strong>{t("order.delivery-terms")}</strong>
                    <p>{t("order.city")}</p>
                    <p>{t("order.regions")}</p>
                  </Alert>

                  <p className="text-sm text-[#6d6e71]">
                    {withPolicyLinks(t("order.consent"), i18n.language)}
                  </p>

                  <Button variant="dark" type="submit" className="eufy-button mr-4 mt-2">
                    {t("order.pay")}
                  </Button>
                  <Button variant="light" type="submit" className="eufy-button eufy-button-secondary mr-4 mt-2">
                    {t("order.instalment")}
                  </Button>
                  {/* <Link to="/cart">
                    <Button variant="secondary" type="button" className="mr-4 mt-2">
                      {t("order.back")}
                    </Button>
                  </Link> */}
                </Form>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
}
